import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { GrSend } from "react-icons/gr";
import { buttonloginstyle2Sm, buttonStyleLink } from "../../help/Utils";
import userImg from "../../assets/user.png";
import backArrow from "../../assets/back-arrow.png";
import { IoDocumentTextOutline } from "react-icons/io5";
import { FaArrowRight } from "react-icons/fa6";
import { FaInfo } from "react-icons/fa6";
import DocumentList from "./DocumentList";
import RequirementCard from "./RequirementCard";
import DocketDownloadCard from "./DocketDownloadCard";
// TODO: Cover letter generation moved to admin docket system — attorneys generate it after approval
// import CoverLetterSection from "./CoverLetterSection";
import {
  getApplication,
  getApplicationForms,
  getSubmissionReadiness,
  submitApplicationForReview,
  type SubmissionReadiness,
  type FormData,
  type FormGroup,
} from "../../API/applicationAPI";
import { getUserFeedbackBatches, type FeedbackBatchDetail } from "../../API/feedbackAPI";
import type { Application } from "../../types/application";
import {
  checkApplicationRequirements,
  downloadDocumentBlob,
  getDocuments,
  type RequirementStatus,
  type UserDocument,
} from "../../API/documentAPI";
import { canAccessUscisFeeFlow } from "../../utils/uscisFeeAccess";
import { toast } from "react-toastify";
import ButtonLoadingLabel from "../../components/ButtonLoadingLabel";
import {
  getFormButtonText,
  type FormCardStatus,
} from "./formCardPresentation";

interface DocumentsListProps {
  title?: string;
  text?: string;
  buttontext: string;
  statusColor: string;
  statusBorderColor: string;
  statusTextColor: string;
  dotColor: string;
  buttonBg: string;
  buttonTextColor: string;
  link?: string;

  status?: FormCardStatus;
  showUnreadBadge?: boolean;
  feedbackPendingCount?: number;
}

interface DocumentUploadsProps {
  title?: string;
  text?: string;
  statusColor: string;
  statusBorderColor: string;
  statusTextColor: string;
  dotColor: string;
  // buttonBg:string;
  // buttonTextColor:string;
  status?: "Pending" | "In Progress" | "Completed" | "Optional" | "Uploaded";
}

function parseFormIdFromLink(link: string | undefined): number | null {
  if (!link) return null;
  const m = link.match(/[?&]formId=(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

function parsePositiveIntParam(raw: string | null): number | null {
  if (raw == null || raw === "") return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function pickFormLinkForPriorityFormIds(
  required: DocumentsListProps[],
  optional: DocumentsListProps[],
  priorityFormIds: readonly number[]
): string | null {
  if (!priorityFormIds.length) return null;
  const set = new Set(priorityFormIds);
  for (const list of [required, optional]) {
    for (const card of list) {
      const fid = parseFormIdFromLink(card.link);
      if (fid != null && set.has(fid)) return card.link ?? null;
    }
  }
  return null;
}

function pickBatchWithPendingForRedirect(batches: FeedbackBatchDetail[]): FeedbackBatchDetail | null {
  const submitted = batches.filter((b) => b.status === "submitted");
  const withPending = submitted.find((b) => b.items?.some((i) => i.status === "pending"));
  return withPending ?? submitted[0] ?? null;
}

function linkForFormId(
  appId: number,
  formId: number,
  required: DocumentsListProps[],
  optional: DocumentsListProps[]
): string {
  for (const card of [...required, ...optional]) {
    const fid = parseFormIdFromLink(card.link);
    if (fid === formId && card.link) return card.link;
  }
  return `/application?applicationId=${appId}&formId=${formId}&formTitle=${encodeURIComponent("Form")}`;
}

/** Loading placeholders matching DocumentList rows (no separate module so builds never miss the file). */
function FormListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="visaCard border border-gray-200 p-4 md:flex block rounded-2xl mb-3 justify-between items-center animate-pulse"
          aria-hidden
        >
          <div className="flex md:w-[70%] w-full md:items-center items-start md:mb-0 mb-4">
            <div className="w-[12px] h-[12px] rounded-full bg-gray-200 md:me-4 me-2 md:mt-0 mt-2 shrink-0" />
            <div className="flex-1 space-y-2 pt-0.5 min-w-0">
              <div className="h-4 bg-gray-200 rounded w-4/5 max-w-md" />
              <div className="h-3 bg-gray-100 rounded w-1/2 max-w-sm" />
            </div>
          </div>
          <div className="flex justify-between items-center gap-3 shrink-0">
            <div className="h-8 w-24 rounded-full bg-gray-200 hidden sm:block" />
            <div className="h-10 w-[120px] rounded-full bg-gray-300" />
          </div>
        </div>
      ))}
    </>
  );
}

const VisaApplication: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const applicationIdParam = searchParams.get("applicationId");
  const [application, setApplication] = useState<Application | null>(null);
  const [loading, setLoading] = useState<boolean>(() => Boolean(applicationIdParam));
  const [error, setError] = useState<string | null>(null);
  const [formsData, setFormsData] = useState<{
    required: DocumentsListProps[];
    optional: DocumentsListProps[];
    documents: DocumentUploadsProps[];
  }>({
    required: [],
    optional: [],
    documents: [],
  });
  const [requiredCount, setRequiredCount] = useState<number>(0);
  const [optionalCount, setOptionalCount] = useState<number>(0);
  const [documentRequirements, setDocumentRequirements] = useState<
    RequirementStatus[]
  >([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<UserDocument[]>([]);
  const [submissionReadiness, setSubmissionReadiness] =
    useState<SubmissionReadiness | null>(null);
  const [loadingRequirements, setLoadingRequirements] = useState<boolean>(false);
  const [submittingForReview, setSubmittingForReview] = useState<boolean>(false);
  const [previewDoc, setPreviewDoc] = useState<UserDocument | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewMime, setPreviewMime] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const previewKind = useMemo<"image" | "pdf" | "video" | "other">(() => {
    const mime = previewMime || "";
    if (mime.startsWith("image/")) return "image";
    if (mime === "application/pdf") return "pdf";
    if (mime.startsWith("video/")) return "video";
    return "other";
  }, [previewMime]);

  const closePreview = useCallback(() => {
    setPreviewDoc(null);
    setPreviewError(null);
    setPreviewLoading(false);
    setPreviewMime(null);
    if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  }, [previewUrl]);

  const handleViewDocument = useCallback(
    async (doc: UserDocument) => {
      const appId = doc.applicationId || application?.id;
      if (!appId) return;

      setPreviewDoc(doc);
      setPreviewError(null);
      setPreviewLoading(true);

      try {
        const detailMime: string = doc.mimeType || "";
        const canPreview =
          detailMime.startsWith("image/") ||
          detailMime === "application/pdf" ||
          detailMime.startsWith("video/");

        if (!canPreview) {
          // Non-previewable types: open via API download (auth stays on our API).
          const blob = await downloadDocumentBlob(appId, doc.id);
          const objectUrl = URL.createObjectURL(blob);
          window.open(objectUrl, "_blank", "noopener,noreferrer");
          // Revoke after the new tab has a chance to load.
          setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
          closePreview();
          return;
        }

        const blob = await downloadDocumentBlob(appId, doc.id);
        const objectUrl = URL.createObjectURL(
          blob.type ? blob : new Blob([blob], { type: detailMime })
        );

        setPreviewMime(detailMime || blob.type || null);
        setPreviewUrl((prev) => {
          if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev);
          return objectUrl;
        });
      } catch (err: any) {
        setPreviewError(err?.message || "Failed to preview document.");
      } finally {
        setPreviewLoading(false);
      }
    },
    [application?.id, closePreview]
  );

  const handlePreviewDownload = async () => {
    if (!previewDoc) return;
    const appId = previewDoc.applicationId || application?.id;
    if (!appId) return;

    try {
      const blob = await downloadDocumentBlob(appId, previewDoc.id);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = previewDoc.fileName || "document";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err: any) {
      console.error("Failed to download document:", err);
      setPreviewError(err?.message || "Failed to download document.");
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Helper function to map API status to UI status
  const mapStatusToUI = (
    status: string,
    isRequired: boolean
  ): DocumentsListProps["status"] => {
    const statusLower = status.toLowerCase();
    if (statusLower === "not_started" || statusLower === "pending")
      return "Not Started";
    if (statusLower === "in_progress") return "In Progress";
    if (
      statusLower === "completed" ||
      statusLower === "reviewed" ||
      statusLower === "approved"
    )
      return "Completed";
    if (!isRequired) return "Optional";
    return "Not Started";
  };

  // Helper function to get status colors based on status
  const getStatusColors = (status: DocumentsListProps["status"]) => {
    switch (status) {
      case "Not Started":
        return {
          statusColor: "#F3F4F6",
          statusBorderColor: "#E5E7EB",
          statusTextColor: "#374151",
          dotColor: "#E5E7EB",
          buttonBg: "#1B365D",
          buttonTextColor: "#FFFFFF",
        };
      case "In Progress":
        return {
          statusColor: "#FEF9C3",
          statusBorderColor: "#FEF9C3",
          statusTextColor: "#A16207",
          dotColor: "#FACC15",
          buttonBg: "#1B365D",
          buttonTextColor: "#FFFFFF",
        };
      case "Completed":
        return {
          statusColor: "#DCFCE7",
          statusBorderColor: "#DCFCE7",
          statusTextColor: "#15803D",
          dotColor: "#22C55E",
          buttonBg: "#F3F4F6",
          buttonTextColor: "#1B365D",
        };
      case "Feedback Received":
        return {
          statusColor: "#FEE2E2",
          statusBorderColor: "#FECACA",
          statusTextColor: "#991B1B",
          dotColor: "#F87171",
          buttonBg: "#1B365D",
          buttonTextColor: "#FFFFFF",
        };
      case "Optional":
        return {
          statusColor: "#DBEAFE",
          statusBorderColor: "#DBEAFE",
          statusTextColor: "#1D4ED8",
          dotColor: "#93C5FD",
          buttonBg: "",
          buttonTextColor: "",
        };
      default:
        return {
          statusColor: "#F3F4F6",
          statusBorderColor: "#E5E7EB",
          statusTextColor: "#374151",
          dotColor: "#E5E7EB",
          buttonBg: "#1B365D",
          buttonTextColor: "#FFFFFF",
        };
    }
  };

  // Convert API form data to UI format
  const convertFormToUI = (
    formData: FormData,
    appId: number,
    opts?: {
      formIdsWithActionRequired?: number[];
      hasUnreadFeedback?: boolean;
      canEditCompletedForm?: boolean;
    }
  ): DocumentsListProps => {
    const pendingFeedback = Number(formData.feedbackPendingItemCount) || 0;

    const baseFormName =
      formData.form?.title ||
      formData.form?.name ||
      formData.form?.formType ||
      formData.form?.formNumber ||
      "Form";

    const formNumber = formData.form?.formNumber;
    const displayTitle =
      formNumber && !baseFormName.includes(formNumber)
        ? `${baseFormName} (${formNumber})`
        : baseFormName;

    const encodedTitle = encodeURIComponent(displayTitle);

    const baseLink =
      appId && formData.form?.id
        ? `/application?applicationId=${appId}&formId=${formData.form.id}&formTitle=${encodedTitle}`
        : "/application";

    if (pendingFeedback > 0) {
      const feedbackLink = `${baseLink}${baseLink.includes("?") ? "&" : "?"}openFeedback=1`;
      const fbStatus: DocumentsListProps["status"] = "Feedback Received";
      return {
        title: displayTitle,
        text: formData.isRequired ? "Required" : "Optional",
        buttontext: getFormButtonText(fbStatus),
        status: fbStatus,
        ...getStatusColors(fbStatus),
        link: feedbackLink,
        showUnreadBadge: false,
        feedbackPendingCount: pendingFeedback,
      };
    }

    const status = mapStatusToUI(formData.status, formData.isRequired);
    const colors = getStatusColors(status);
    const buttonText = getFormButtonText(
      status,
      opts?.canEditCompletedForm,
    );

    return {
      title: displayTitle,
      text: formData.isRequired ? "Required" : "Optional",
      buttontext: buttonText,
      status,
      ...colors,
      link: baseLink,
      showUnreadBadge:
        status === "Completed" &&
        !!(
          opts?.hasUnreadFeedback &&
          opts?.formIdsWithActionRequired &&
          formData.form?.id &&
          opts.formIdsWithActionRequired.includes(formData.form.id)
        ),
    };
  };

  // Fetch document requirements. Initial load shows the section skeleton;
  // post-upload refreshes pass `{ silent: true }` so cards stay mounted and
  // the user can immediately pick the next file (e.g. PRC Front, then Back).
  const fetchDocumentRequirements = async (
    applicationId: number,
    options: { silent?: boolean } = {},
  ) => {
    const silent = Boolean(options.silent);
    try {
      if (!silent) setLoadingRequirements(true);
      const readiness = await getSubmissionReadiness(applicationId);
      setSubmissionReadiness(readiness);
      if (!readiness.documentsUnlocked) {
        setDocumentRequirements([]);
        setUploadedDocuments([]);
        return;
      }
      const [requirementsStatus, docs] = await Promise.all([
        checkApplicationRequirements(applicationId),
        getDocuments(applicationId).catch(() => [] as UserDocument[]),
      ]);
      setDocumentRequirements(requirementsStatus.requirements);
      setUploadedDocuments(docs);
    } catch (error: any) {
      console.error("Error loading document requirements:", error);
      // Don't block the page if requirements fail to load
    } finally {
      if (!silent) setLoadingRequirements(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!applicationIdParam) {
        setLoading(false);
        return;
      }
      const appId = parseInt(applicationIdParam);
      try {
        setLoading(true);
        setLoadingRequirements(true);
        setError(null);

        // Fire overview fetches in parallel. The application is
        // the only one that gates the page (its `stage` decides whether to
        // redirect to the USCIS-fee flow). Documents are fetched only after
        // readiness says uploads are unlocked.
        const [appResult, readinessResult, formsResult] = await Promise.allSettled([
          getApplication(appId),
          getSubmissionReadiness(appId),
          getApplicationForms(appId),
        ]);

        // The application fetch is load-blocking; failure aborts the page.
        if (appResult.status === "rejected") {
          throw appResult.reason;
        }
        const app = appResult.value;

        // Redirect if in USCIS fee stages (only when eligible for that
        // flow). Triggered for deep-linked / bookmarked visits — the
        // dashboard itself never routes a fee-stage application here.
        if (
          (app.stage === "uscis_fee_decision" ||
            app.stage === "uscis_fee_questionnaire") &&
          canAccessUscisFeeFlow(app)
        ) {
          navigate(`/application/${app.id}/uscis-fee`);
          return;
        }

        setApplication(app);

        // Submission readiness gates document upload visibility. Card
        // components join requirements ↔ docs client-side via
        // applicationDocumentRequirementId after documents unlock.
        if (app.id && app.visaType) {
          if (readinessResult.status === "fulfilled") {
            setSubmissionReadiness(readinessResult.value);
            if (readinessResult.value.documentsUnlocked) {
              await fetchDocumentRequirements(app.id);
            } else {
              setDocumentRequirements([]);
              setUploadedDocuments([]);
              setLoadingRequirements(false);
            }
          } else {
            console.error(
              "Error loading submission readiness:",
              readinessResult.reason,
            );
            setLoadingRequirements(false);
          }
        }

        // Forms — non-blocking; fall through on failure. The previous
        // stage-list condition collapsed to `typeof app.stage === "string"`
        // (i.e. always true when the stage is set), so we now just check
        // we have a successful response and process it the same way.
        if (formsResult.status === "fulfilled") {
          try {
            const formsResponse = formsResult.value;
            const canEditCompletedForm =
              readinessResult.status === "fulfilled" &&
              !readinessResult.value.alreadySubmitted;

            // Separate required and optional forms
            const required: DocumentsListProps[] = [];
            const optional: DocumentsListProps[] = [];
            const documents: DocumentUploadsProps[] = [];

            // Process forms by category
            formsResponse.forms.forEach((group: FormGroup) => {
              if (group.category === "Application") {
                // Application forms go to required/optional sections
                group.forms.forEach((formData: FormData) => {
                  const uiForm = convertFormToUI(formData, app.id, {
                    formIdsWithActionRequired: app.formIdsWithActionRequired,
                    hasUnreadFeedback: app.hasUnreadFeedback,
                    canEditCompletedForm,
                  });
                  if (formData.isRequired) {
                    required.push(uiForm);
                  } else {
                    optional.push(uiForm);
                  }
                });
              } else if (group.category === "Supporting Document") {
                // Supporting documents can be forms or documents
                group.forms.forEach((formData: FormData) => {
                  // Check if it's a form (has formType) or a document
                  if (formData.form.formType) {
                    const uiForm = convertFormToUI(formData, app.id, {
                      formIdsWithActionRequired: app.formIdsWithActionRequired,
                      hasUnreadFeedback: app.hasUnreadFeedback,
                      canEditCompletedForm,
                    });
                    if (formData.isRequired) {
                      required.push(uiForm);
                    } else {
                      optional.push(uiForm);
                    }
                  } else {
                    // It's a document for upload
                    const mappedStatus = mapStatusToUI(
                      formData.status,
                      formData.isRequired,
                    );
                    // Convert "Not Started" to "Pending" for document uploads
                    const docStatus: DocumentUploadsProps["status"] =
                      mappedStatus === "Not Started"
                        ? "Pending"
                        : mappedStatus === "In Progress"
                          ? "In Progress"
                          : mappedStatus === "Completed"
                            ? "Completed"
                            : "Pending";
                    const colors = getStatusColors(mappedStatus);

                    documents.push({
                      title: formData.form.name || "Document",
                      text: formData.form.description || "",
                      status: docStatus,
                      statusColor: colors.statusColor,
                      statusBorderColor: colors.statusBorderColor,
                      statusTextColor: colors.statusTextColor,
                      dotColor: colors.dotColor,
                    });
                  }
                });
              } else if (group.category === "Document Upload") {
                // Documents for upload section
                group.forms.forEach((formData: FormData) => {
                  // Some datasets include actual "forms" under the "Document Upload"
                  // grouping. Those should contribute to required/optional counts.
                  const hasFormType =
                    formData.form?.formType || formData.form?.formNumber;

                  if (hasFormType) {
                    const uiForm = convertFormToUI(formData, app.id, {
                      formIdsWithActionRequired: app.formIdsWithActionRequired,
                      hasUnreadFeedback: app.hasUnreadFeedback,
                      canEditCompletedForm,
                    });

                    if (formData.isRequired) {
                      required.push(uiForm);
                    } else {
                      optional.push(uiForm);
                    }

                    return;
                  }

                  const mappedStatus = mapStatusToUI(
                    formData.status,
                    formData.isRequired,
                  );
                  // Convert "Not Started" to "Pending" for document uploads
                  const docStatus: DocumentUploadsProps["status"] =
                    mappedStatus === "Not Started"
                      ? "Pending"
                      : mappedStatus === "In Progress"
                        ? "In Progress"
                        : mappedStatus === "Completed"
                          ? "Completed"
                          : "Pending";
                  const colors = getStatusColors(mappedStatus);

                  documents.push({
                    title: formData.form.name || "Document",
                    text: formData.form.description || "",
                    status: docStatus,
                    statusColor: colors.statusColor,
                    statusBorderColor: colors.statusBorderColor,
                    statusTextColor: colors.statusTextColor,
                    dotColor: colors.dotColor,
                  });
                });
              } else {
                // Handle all other categories (Payment, Notification, Fee Waiver, etc.)
                // These are forms that should go to required/optional sections
                group.forms.forEach((formData: FormData) => {
                  // Check if it's a form (has formType or formNumber) or a document
                  const hasFormType =
                    formData.form?.formType || formData.form?.formNumber;
                  if (hasFormType) {
                    const uiForm = convertFormToUI(formData, app.id, {
                      formIdsWithActionRequired: app.formIdsWithActionRequired,
                      hasUnreadFeedback: app.hasUnreadFeedback,
                      canEditCompletedForm,
                    });
                    if (formData.isRequired) {
                      required.push(uiForm);
                    } else {
                      optional.push(uiForm);
                    }
                  } else {
                    // It's a document for upload
                    const mappedStatus = mapStatusToUI(
                      formData.status,
                      formData.isRequired,
                    );
                    // Convert "Not Started" to "Pending" for document uploads
                    const docStatus: DocumentUploadsProps["status"] =
                      mappedStatus === "Not Started"
                        ? "Pending"
                        : mappedStatus === "In Progress"
                          ? "In Progress"
                          : mappedStatus === "Completed"
                            ? "Completed"
                            : "Pending";
                    const colors = getStatusColors(mappedStatus);

                    documents.push({
                      title:
                        formData.form.name || formData.form.title || "Document",
                      text: formData.form.description || "",
                      status: docStatus,
                      statusColor: colors.statusColor,
                      statusBorderColor: colors.statusBorderColor,
                      statusTextColor: colors.statusTextColor,
                      dotColor: colors.dotColor,
                    });
                  }
                });
              }
            });

            setFormsData({ required, optional, documents });
            setRequiredCount(required.length);
            setOptionalCount(optional.length);
          } catch (formsError: any) {
            console.error("Error loading forms:", formsError);
            // Don't block the page if forms fail to load, just log the error
            // The component will use empty arrays which will show no forms
          }
        } else {
          console.error("Error loading forms:", formsResult.reason);
        }
      } catch (err: any) {
        console.error("Error loading application:", err);
        setError(
          err?.response?.data?.message ||
            "Failed to load application. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [applicationIdParam, navigate]);

  // openFeedback=1: land on the form that actually has feedback (not always the first card).
  useEffect(() => {
    const openFeedback = searchParams.get("openFeedback") === "1";
    if (!openFeedback || !application?.id || loading) return;

    let cancelled = false;

    const appendOpenFeedbackParams = (base: string) => {
      const sep = base.includes("?") ? "&" : "?";
      const params = new URLSearchParams();
      params.set("openFeedback", "1");
      const questionId = searchParams.get("feedbackQuestionId");
      const itemId = searchParams.get("feedbackItemId");
      if (questionId) params.set("feedbackQuestionId", questionId);
      if (itemId) params.set("feedbackItemId", itemId);
      return `${base}${sep}${params.toString()}`;
    };

    (async () => {
      const appId = application.id;
      const { required, optional } = formsData;
      let targetLink: string | null = null;

      try {
        const { batches } = await getUserFeedbackBatches(appId);

        const itemIdParam = parsePositiveIntParam(searchParams.get("feedbackItemId"));
        if (itemIdParam != null) {
          for (const b of batches) {
            const hit = b.items?.find((i) => i.id === itemIdParam);
            if (hit && b.formId != null && b.formId > 0) {
              targetLink = linkForFormId(appId, b.formId, required, optional);
              break;
            }
          }
        }

        if (!targetLink) {
          const questionIdParam = parsePositiveIntParam(searchParams.get("feedbackQuestionId"));
          if (questionIdParam != null) {
            for (const b of batches) {
              const hit = b.items?.find((i) => i.questionId === questionIdParam);
              if (hit && b.formId != null && b.formId > 0) {
                targetLink = linkForFormId(appId, b.formId, required, optional);
                break;
              }
            }
          }
        }

        if (!targetLink) {
          const priority = application.formIdsWithActionRequired ?? [];
          targetLink = pickFormLinkForPriorityFormIds(required, optional, priority);
        }

        if (!targetLink) {
          const batch = pickBatchWithPendingForRedirect(batches);
          if (!cancelled && batch?.formId != null && batch.formId > 0) {
            targetLink = linkForFormId(appId, batch.formId, required, optional);
          }
        }

        if (!targetLink) {
          const first = required[0] || optional[0];
          targetLink = first?.link ?? null;
        }

        if (!cancelled && targetLink) {
          navigate(appendOpenFeedbackParams(targetLink), { replace: true });
        }
      } catch {
        const first = required[0] || optional[0];
        if (!cancelled && first?.link) {
          navigate(appendOpenFeedbackParams(first.link), { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    searchParams,
    application?.id,
    application?.formIdsWithActionRequired,
    formsData.required,
    formsData.optional,
    loading,
    navigate,
  ]);

  // Use dynamic data if available, otherwise fallback to empty arrays
  const cardsData = formsData.required.length > 0 ? formsData.required : [];
  const cardsoptional = formsData.optional.length > 0 ? formsData.optional : [];
  const showDocumentUploadsSection =
    !loading &&
    !!application?.id &&
    !!(
      submissionReadiness?.documentsUnlocked ||
      submissionReadiness?.formsReady ||
      submissionReadiness?.alreadySubmitted
    );

  // focus=documents: scroll to Document Uploads (dashboard deep link).
  useEffect(() => {
    if (searchParams.get("focus") !== "documents") return;
    if (loading || loadingRequirements) return;
    if (!showDocumentUploadsSection) return;
    const el = document.getElementById("document-uploads");
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [
    searchParams,
    loading,
    loadingRequirements,
    showDocumentUploadsSection,
    documentRequirements.length,
    uploadedDocuments.length,
  ]);

  // focus=docket: scroll to the filing packet download (Basic "Ready to File").
  useEffect(() => {
    if (searchParams.get("focus") !== "docket") return;
    if (loading || !application?.id) return;
    const el = document.getElementById("filing-packet");
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [searchParams, loading, application?.id]);

  const showSubmitForReviewSection =
    !loading &&
    !!application?.id &&
    !!submissionReadiness?.canSubmit &&
    !submissionReadiness?.alreadySubmitted;

  const handleSubmitForReview = async () => {
    if (!application?.id || submittingForReview) return;
    try {
      setSubmittingForReview(true);
      const result = await submitApplicationForReview(application.id);
      setApplication(result.application);
      setSubmissionReadiness(result.readiness);
      toast.success(
        result.alreadySubmitted
          ? "Application is already in review."
          : "Application submitted for review."
      );
    } catch (err: any) {
      toast.error(err?.message || "Failed to submit application for review.");
      if (application.id) {
        fetchDocumentRequirements(application.id);
      }
    } finally {
      setSubmittingForReview(false);
    }
  };

  // Show error state
  if (error) {
    return (
      <div className="page_main page_main-after-login mt-[100px] pt-[40px] bg-[#F7F8FA] pb-10">
        <div className="custom-container">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-[#1B365D] mb-2">
              Error Loading Application
            </h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <Link
              to="/dashboard"
              className="bg-[#F4A261] text-[#1B365D] font-semibold px-6 py-3 rounded-[50px] hover:bg-[#F29449] inline-block"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page_main page_main-after-login mt-[100px] pt-[40px] bg-[#F7F8FA] pb-10">
      <div className="custom-container">
        <Link to="/dashboard" className="font-bold flex items-center">
          <img src={backArrow} alt="back arrow" className="me-2" /> Back to
          Dashboard
        </Link>
        <div className="md:flex items-center justify-between mt-10">
          <div>
            <h1 className="heading-lg">Visa Application</h1>
            {loading && !application ? (
              <div className="text-[16px] mt-2 space-y-2 animate-pulse max-w-md" aria-busy="true">
                <div className="h-4 bg-gray-200 rounded w-48" />
                <div className="h-4 bg-gray-100 rounded w-64" />
              </div>
            ) : (
              <p className="text-[16px] mt-2">
                {application?.visaType || "N-400"}
                {application?.id ? ` • Application ID: ${application.id}` : ""}
              </p>
            )}
          </div>
        </div>

        {application?.id ? (
          <div className="mt-8" id="filing-packet">
            <DocketDownloadCard applicationId={application.id} />
          </div>
        ) : null}

        <div className="md:px-[50px] ">
          <div className="bg-[#80EFD9] md:p-[32px]  p-[15px] rounded-[12px] flex mt-10">
            <div className="bg-[#1B365D0D] w-[48px] h-[48px] rounded-[8px] flex items-center justify-center me-5">
              <FaInfo />
            </div>
            {application?.uscisFee?.canPayNow ? (
              <div className="w-[90%]">
                <h6 className="text-[18px] md:text-[24px] leading-[32px] text-[#1B365D] font-SemiBold mb-2">
                  Pay your USCIS filing fee
                </h6>
                <p className="text-[14px] md:text-[18px] leading-[20px] md:leading-[30px] text-[#1B365DCC] mb-4">
                  You chose to pay the USCIS filing fee later. Pay it now to keep
                  your filing on track — this only charges the USCIS fee, not your
                  package.
                </p>
                <button
                  type="button"
                  onClick={() => navigate(`/application/${application.id}/uscis-fee`)}
                  className="bg-[#1B365D] text-white font-SemiBold text-[14px] md:text-[16px] px-6 py-3 rounded-[8px] hover:opacity-90 transition"
                >
                  Pay USCIS fee →
                </button>
              </div>
            ) : (
              <div className="w-[90%]">
                <h6 className="text-[18px] md:text-[24px] leading-[32px] text-[#1B365D] font-SemiBold mb-2">
                  Welcome to Your Application Dashboard
                </h6>
                <p className="text-[14px] md:text-[18px] eading-[20px] md:eading-[30px] text-[#1B365DCC]">
                  Complete the required forms and upload necessary documents step
                  by step. After finishing one form, return here to continue with
                  the next. Your progress is automatically saved.
                </p>
              </div>
            )}
          </div>
        </div>

        {showSubmitForReviewSection ? (
          <div className="containersm mx-auto px-4 mt-12">
            <div className="border border-[#E5E7EB] rounded-[16px] bg-white p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-[20px] font-SemiBold text-[#1B365D]">
                  Submit your application for review
                </h2>
                <p className="text-sm text-[#6B7280] mt-1">
                  {submissionReadiness?.canSubmit
                    ? "Required forms and document uploads are complete. Submit when you are ready for our team to review the package."
                    : !submissionReadiness?.formsReady
                      ? "Complete all required forms first. Document uploads unlock after the required forms are marked complete."
                      : "Upload the required documents before submitting the application for review."}
                </p>
                {!submissionReadiness?.canSubmit ? (
                  <p className="text-xs text-[#9CA3AF] mt-2">
                    {submissionReadiness?.missingForms?.length
                      ? `${submissionReadiness.missingForms.length} required form(s) still incomplete.`
                      : submissionReadiness?.missingDocuments?.length
                        ? `${submissionReadiness.missingDocuments.length} required document item(s) still missing.`
                        : "Checking readiness..."}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={handleSubmitForReview}
                disabled={submittingForReview || !submissionReadiness?.canSubmit}
                className="bg-[#1B365D] text-white font-SemiBold text-[14px] md:text-[16px] px-6 py-3 rounded-[8px] hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingForReview ? (
                  <ButtonLoadingLabel label="Submitting" />
                ) : (
                  "Submit for review"
                )}
              </button>
            </div>
          </div>
        ) : null}
      </div>
      <div className="containersm mx-auto px-4 pb-10">
        <div className="mt-10 border border-[#E5E7EB] rounded-[16px] bg-white ">
          <div className=" flex items-center  border-b border-[#E5E7EB] p-[24px]">
            <p className="md:w-[32px] w-[26px] md:h-[32px] h-[26px] rounded-[8px] bg-[#FEE2E2] text-center md:text-base text-[12px] text-[#DC2626] flex items-center justify-center ">
              1
            </p>
            <h5 className="mx-4 font-SemiBold md:text-[20px] text-[18px] md:w-auto w-[45%]">
              Required Forms
            </h5>{" "}
            <label className="bg-[#FEE2E2] rounded-[50px] px-4 py-1 md:text-sm text-[12px] text-[#DC2626] flex items-center justify-center min-h-[28px]">
              {loading ? (
                <span className="inline-block w-28 h-4 bg-red-200/70 rounded-full animate-pulse" aria-hidden />
              ) : requiredCount > 0 ? (
                `${requiredCount} Required`
              ) : (
                "0 Required"
              )}
            </label>
          </div>
          <div className="p-[24px]">
            {loading ? (
              <FormListSkeleton count={3} />
            ) : (
              cardsData.map((card, index) => (
                <DocumentList
                  key={index}
                  title={card.title}
                  text={card.text}
                  status={card.status}
                  buttontext={card.buttontext}
                  statusColor={card.statusColor}
                  statusBorderColor={card.statusBorderColor}
                  statusTextColor={card.statusTextColor}
                  dotColor={card.dotColor}
                  buttonBg={card.buttonBg}
                  buttonTextColor={card.buttonTextColor}
                  link={card.link}
                  showUnreadBadge={card.showUnreadBadge}
                  feedbackPendingCount={card.feedbackPendingCount}
                />
              ))
            )}
          </div>
        </div>

        <div className="mt-5 border border-[#E5E7EB] rounded-[16px] bg-white">
          <div className=" flex items-center border-b border-[#E5E7EB] p-[24px]">
            <p className="md:w-[32px] w-[26px] md:h-[32px] h-[26px] rounded-[8px] bg-[#DBEAFE] text-center md:text-base text-[12px] text-[#2563EB] flex items-center justify-center">
              2
            </p>{" "}
            <h5 className="mx-4 font-SemiBold md:text-[20px] text-[18px] md:w-auto w-[45%]">
              Optional Forms (If Applicable)
            </h5>{" "}
            <label className="bg-[#DBEAFE] rounded-[50px] px-4 py-1 md:text-sm text-[12px]  text-[#1D4ED8] flex items-center justify-center min-h-[28px]">
              {loading ? (
                <span className="inline-block w-28 h-4 bg-blue-200/70 rounded-full animate-pulse" aria-hidden />
              ) : optionalCount > 0 ? (
                `${optionalCount} Optional`
              ) : (
                "0 Optional"
              )}
            </label>
          </div>
          <div className="p-[24px]">
            {loading ? (
              <FormListSkeleton count={2} />
            ) : (
              cardsoptional.map((card, index) => (
                <DocumentList
                  key={index}
                  title={card.title}
                  text={card.text}
                  status={card.status}
                  buttontext={card.buttontext}
                  statusColor={card.statusColor}
                  statusBorderColor={card.statusBorderColor}
                  statusTextColor={card.statusTextColor}
                  dotColor={card.dotColor}
                  buttonBg={card.buttonBg}
                  buttonTextColor={card.buttonTextColor}
                  link={card.link}
                  showUnreadBadge={card.showUnreadBadge}
                  feedbackPendingCount={card.feedbackPendingCount}
                />
              ))
            )}
          </div>
        </div>

        {showDocumentUploadsSection ? (
          <div
            id="document-uploads"
            className="mt-5 border border-[#E5E7EB] rounded-[16px] bg-white"
          >
            <div className="flex items-center justify-between border-b border-[#E5E7EB] p-[24px]">
              <div className="flex items-center flex-wrap gap-2">
                <p className="md:w-[32px] w-[26px] md:h-[32px] h-[26px] rounded-[8px] bg-[#DCFCE7] text-center md:text-base text-[12px] text-[#16A34A] flex items-center justify-center">
                  3
                </p>
                <h5 className="mx-2 font-SemiBold md:text-[20px] md:w-auto text-[18px]">
                  Document Uploads
                </h5>
                <label className="bg-[#DCFCE7] rounded-[50px] px-4 py-1 md:text-sm text-[12px] text-[#16A34A] flex items-center justify-center min-h-[28px]">
                  {loadingRequirements || (loading && documentRequirements.length === 0) ? (
                    <span className="inline-block w-32 h-4 bg-green-200/70 rounded-full animate-pulse" aria-hidden />
                  ) : documentRequirements.length > 0 ? (
                    `${documentRequirements.length} Categories`
                  ) : (
                    "0 Categories"
                  )}
                </label>
                {(() => {
                  const docFbCount = uploadedDocuments.filter(
                    (d) => d.status === "needs_reupload" || d.status === "rejected"
                  ).length;
                  if (docFbCount <= 0) return null;
                  return (
                    <span
                      className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-600 text-white text-xs font-semibold flex items-center justify-center"
                      title={`${docFbCount} document${docFbCount === 1 ? "" : "s"} need attention`}
                      aria-label={`${docFbCount} documents need attention`}
                    >
                      {docFbCount > 99 ? "99+" : docFbCount}
                    </span>
                  );
                })()}
              </div>
            </div>
            <div className="p-[24px]">
              {loadingRequirements ? (
                <FormListSkeleton count={2} />
              ) : documentRequirements.length > 0 ? (
                documentRequirements.map((req) => (
                  <RequirementCard
                    key={req.requirementId}
                    requirement={req}
                    documents={uploadedDocuments.filter(
                      (d) => d.applicationDocumentRequirementId === req.requirementId
                    )}
                    applicationId={application?.id || 0}
                    onChange={() => {
                      if (application?.id) {
                        fetchDocumentRequirements(application.id, { silent: true });
                      }
                    }}
                    onView={handleViewDocument}
                    disabled={submissionReadiness?.alreadySubmitted}
                    disabledReason="This application has been submitted for review. Document changes are locked while our team reviews it."
                  />
                ))
              ) : loading ? (
                <FormListSkeleton count={2} />
              ) : (
                <div className="text-center py-8">
                  <IoDocumentTextOutline className="text-5xl text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">
                    No document requirements configured for this visa type.
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    Document requirements will appear here once configured.
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : null}
        {/* Cover letter generation removed from user portal — will be part of admin docket system */}
        {/* {application?.id && !loading && (
          <CoverLetterSection applicationId={application.id} />
        )} */}
      </div>

      {previewDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-[16px] w-[90%] max-w-[900px] max-h-[85vh] p-6 relative">
            <button
              className="absolute top-4 right-4 w-[36px] h-[36px] rounded-full border border-gray-200 flex items-center justify-center text-xl"
              onClick={closePreview}
              aria-label="Close preview"
            >
              ×
            </button>
            <h3 className="text-xl font-semibold text-[#1B365D] mb-4">{previewDoc.fileName}</h3>
            <div className="bg-gray-50 rounded-[12px] p-4 max-h-[70vh] overflow-auto">
              {previewLoading ? (
                <div className="py-10 flex justify-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1B365D]" />
                </div>
              ) : previewError ? (
                <div className="text-center text-red-600 py-10">
                  <p className="mb-4">{previewError}</p>
                  <button
                    className="border border-[#1B365D] text-[#1B365D] px-6 py-2 rounded-[50px] hover:bg-[#1B365D] hover:text-white transition-colors"
                    onClick={handlePreviewDownload}
                  >
                    Download instead
                  </button>
                </div>
              ) : previewKind === "image" ? (
                <img
                  src={previewUrl || ""}
                  alt={previewDoc.fileName}
                  className="max-h-[60vh] mx-auto object-contain"
                />
              ) : previewKind === "pdf" ? (
                <iframe
                  title={previewDoc.fileName}
                  src={previewUrl || ""}
                  className="w-full h-[60vh] rounded-[10px] border"
                />
              ) : previewKind === "video" ? (
                <video
                  controls
                  className="w-full max-h-[60vh] rounded-[10px] border bg-black"
                  src={previewUrl || ""}
                />
              ) : (
                <div className="text-center text-gray-600 py-10">
                  Preview not available. Use Download to view this file.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisaApplication;
